import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
    Autocomplete,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import '../css/crmDashboard.css';
import '../css/crmClientTabs.css';
import '../css/crmLeadDetails.css';
import '../css/crmLeadContract.css';
import DadosUsuario from '../../utils/context';
import { getPhoneDigits, maskCEP, maskCpfCnpj, maskPhone } from '../../utils/maskCpfCnpj';
import { getDigits } from '../../utils/stringFormat';
import { Field, Pill } from './crmClientTabs';
import {
    CIDADES_POR_ESTADO,
    CLASSIFICATION_OPTIONS,
    CONTRACT_STEPS,
    EMPTY_ADDRESS,
    EMPTY_REPRESENTATIVE,
    ESTADOS_ATENDIDOS,
    ESTADOS_BRASIL,
    MARITAL_STATUS_OPTIONS,
    VERIFICATION_OPTIONS,
    buildContractFormData,
    fetchAddressByCep,
    fetchCnpjCardData,
    fetchLeadUnits,
    formatUnitAddress,
    getAddressFromUnit,
    getClassificationFromUnits,
    getDistribuidoraFromState,
    getFirstName,
    getLeadClientType,
    getLeadCoupon,
    getLeadDiscountPercent,
    getMissingAddressFields,
    getMissingRepresentativeFields,
    getUnitKey,
    isCnpjComplete,
    isCpfComplete,
    isFilled,
    joinLabels,
    submitContract
} from './crmLeadContractData';
import { fetchCrmLeadById } from './crmLeadsData';

const SERASA_AGAIN_NOTICE = 'Clique em Gerar contrato para consultar o Serasa novamente.';

const selectMenuProps = {
    PaperProps: { className: 'crm-dashboard-select-menu-paper' },
    MenuListProps: { className: 'crm-dashboard-select-menu-list' }
};

const editableFieldClass = 'crm-lead-details-text-field crm-lead-details-text-field-editable';
const selectClass = 'crm-lead-details-select crm-dashboard-filter-control crm-dashboard-bordered-select';

const displayValue = (value) => (isFilled(value) ? value : '—');

const formatConsumption = (value) => {
    const number = Number(value);
    return isFilled(value) && Number.isFinite(number) ? `${number.toLocaleString('pt-BR')} kWh` : '—';
};

const getClassificationLabel = (value) => CLASSIFICATION_OPTIONS.find((option) => option.value === value)?.label ?? '—';

const getVerificationLabel = (value) => VERIFICATION_OPTIONS.find((option) => option.value === value)?.label ?? '—';

const formatAddressLine = (address) => [
    [address.street, address.number].filter(isFilled).join(', '),
    address.complement,
    address.neighborhood,
    [address.city, address.state].filter(isFilled).join('/'),
    address.cep
].filter(isFilled).join(' · ');

const SelectField = ({ id, label, value, onChange, options, required = false }) => (
    <FormControl className={selectClass} fullWidth required={required}>
        <InputLabel id={`${id}-label`}>{label}</InputLabel>
        <Select
            labelId={`${id}-label`}
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            IconComponent={KeyboardArrowDownRoundedIcon}
            MenuProps={selectMenuProps}
        >
            {options.map((option) => (
                <MenuItem className='crm-dashboard-select-menu-item' key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

const FileField = ({ label, file, onChange, accept = '*/*' }) => (
    <div className='crm-lead-contract-file'>
        <span className='crm-lead-contract-file-label'>{label}</span>
        <TextField
            className='crm-lead-details-text-field crm-lead-details-upload-input'
            type='file'
            size='small'
            fullWidth
            inputProps={{ accept }}
            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
        {file && <span className='crm-lead-contract-file-name'>{file.name}</span>}
    </div>
);

const Block = ({ icon: Icon, title, actions, children }) => (
    <section className='crm-client-block'>
        <div className='crm-client-block-head'>
            <h3 className='crm-client-block-title'>
                {Icon && <Icon fontSize='inherit' />}
                {title}
            </h3>
            {actions}
        </div>
        {children}
    </section>
);

const CrmLeadContract = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { leadId } = useParams();
    const queryClient = useQueryClient();
    const dadosUsuario = useContext(DadosUsuario);
    const canChooseClientNotification = dadosUsuario.email === 'maurofarias@settaenergia.com.br' ||
        dadosUsuario.autorizacoes?.includes('Adm');

    const [routeState] = useState(() => location.state ?? {});
    const leadListState = routeState.leadListState;
    const hasRouteLead = Boolean(routeState.leadData) && String(routeState.leadData.lead_id) === String(leadId);
    const isSerasaApproved = routeState.serasaAprovado === true && (!routeState.leadData || hasRouteLead);
    const leadPath = `/CRM/leads/${leadId}`;

    const [leadData, setLeadData] = useState(() => (hasRouteLead ? routeState.leadData : null));
    const [leadUnits, setLeadUnits] = useState(() => (
        hasRouteLead && Array.isArray(routeState.leadUnits) ? routeState.leadUnits : null
    ));
    const [loadState, setLoadState] = useState(() => (
        hasRouteLead && Array.isArray(routeState.leadUnits) ? 'ready' : 'loading'
    ));
    const [reloadKey, setReloadKey] = useState(0);
    const initializedRef = useRef(false);

    const [step, setStep] = useState(0);
    const [maxStepReached, setMaxStepReached] = useState(0);
    const [maritalStatus, setMaritalStatus] = useState('');
    const [address, setAddress] = useState(EMPTY_ADDRESS);
    const [representative, setRepresentative] = useState(EMPTY_REPRESENTATIVE);
    const [files, setFiles] = useState({ cnh: null, contrato: null, cnpj: null });
    const [cnpjCardName, setCnpjCardName] = useState('');
    const [selectedUnitKeys, setSelectedUnitKeys] = useState([]);
    const [classification, setClassification] = useState('');
    const [isClassificationTouched, setIsClassificationTouched] = useState(false);
    const [verificationType, setVerificationType] = useState('');
    const [contactName, setContactName] = useState('');
    const [phone2, setPhone2] = useState('');
    const [notificarCliente, setNotificarCliente] = useState(true);
    const [isFetchingCep, setIsFetchingCep] = useState(false);
    const [isFetchingCnpj, setIsFetchingCnpj] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [message, setMessage] = useState(() => routeState.serasaMessage ?? '');
    const [statusAlerta, setStatusAlerta] = useState(() => routeState.serasaStatus ?? 'success');
    const [openAlerta, setOpenAlerta] = useState(() => Boolean(routeState.serasaMessage));

    const showAlert = (text, status) => {
        setMessage(text);
        setStatusAlerta(status);
        setOpenAlerta(true);
    };

    const goToLead = (state = {}) => navigate(leadPath, { state: { leadListState, ...state } });

    useEffect(() => {
        if (!isSerasaApproved) {
            navigate(leadPath, {
                replace: true,
                state: { leadListState, leadNotice: { message: SERASA_AGAIN_NOTICE, status: 'warning' } }
            });
            return;
        }
        navigate(location.pathname, { replace: true, state: { leadListState } });
    }, []);

    useEffect(() => {
        if (!isSerasaApproved || loadState === 'ready') return undefined;

        let ignore = false;
        setLoadState('loading');

        const load = async () => {
            try {
                const lead = leadData ?? await fetchCrmLeadById(leadId);
                if (ignore) return;
                if (!lead) {
                    setLoadState('not_found');
                    return;
                }

                const units = leadUnits ?? await fetchLeadUnits(lead.lead_id);
                if (ignore) return;

                setLeadData(lead);
                setLeadUnits(units);
                setLoadState('ready');
            } catch (error) {
                if (ignore) return;
                setLoadState('error');
                showAlert(error.message, 'error');
            }
        };

        load();
        return () => { ignore = true; };
    }, [reloadKey]);

    const clientType = leadData ? getLeadClientType(leadData.cpf) : 'pf';
    const isPessoaJuridica = clientType === 'pj';
    const clientName = isPessoaJuridica && cnpjCardName ? cnpjCardName : (leadData?.nome ?? '');
    const units = useMemo(() => leadUnits ?? [], [leadUnits]);
    const selectedUnits = useMemo(
        () => units.filter((unit, index) => selectedUnitKeys.includes(getUnitKey(unit, index))),
        [selectedUnitKeys, units]
    );
    const selectedConsumption = selectedUnits.reduce((total, unit) => total + (Number(unit.consumo_medio) || 0), 0);

    useEffect(() => {
        if (loadState !== 'ready' || !leadData || initializedRef.current) return;
        initializedRef.current = true;

        const allUnitKeys = units.map((unit, index) => getUnitKey(unit, index));
        setSelectedUnitKeys(allUnitKeys);
        setClassification(getClassificationFromUnits(units));
        setContactName(getFirstName(leadData.nome));
        if (getLeadClientType(leadData.cpf) === 'pf') {
            setAddress(getAddressFromUnit(units[0]));
        }
    }, [leadData, loadState, units]);

    useEffect(() => {
        if (!initializedRef.current || isClassificationTouched || selectedUnits.length === 0) return;
        setClassification(getClassificationFromUnits(selectedUnits));
    }, [isClassificationTouched, selectedUnits]);

    const updateAddress = (field, value) => setAddress((prev) => ({ ...prev, [field]: value }));
    const updateRepresentative = (field, value) => setRepresentative((prev) => ({ ...prev, [field]: value }));
    const updateFile = (field, file) => setFiles((prev) => ({ ...prev, [field]: file }));

    const toggleUnit = (key) => {
        setSelectedUnitKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
    };

    const searchCep = async () => {
        if (getDigits(address.cep).length !== 8) {
            showAlert('Informe o CEP completo para buscar o endereço.', 'error');
            return;
        }

        setIsFetchingCep(true);
        const { address: found, error } = await fetchAddressByCep(address.cep);
        if (error) {
            showAlert(error.message ?? 'CEP não encontrado.', error.status ?? 'error');
        } else {
            setAddress((prev) => ({ ...prev, ...found }));
        }
        setIsFetchingCep(false);
    };

    const readCnpjCard = async () => {
        if (!files.cnpj) {
            showAlert('Faça o upload do cartão CNPJ.', 'error');
            return;
        }

        setIsFetchingCnpj(true);
        const result = await fetchCnpjCardData(files.cnpj);
        setIsFetchingCnpj(false);

        if (result.error) {
            showAlert(result.error.message ?? 'Não foi possível ler o cartão CNPJ.', result.error.status ?? 'error');
            return;
        }

        if (getDigits(result.cnpj) && getDigits(result.cnpj) !== getDigits(leadData.cpf)) {
            showAlert(
                `O CNPJ do cartão (${result.cnpj}) é diferente do CNPJ do lead (${maskCpfCnpj(leadData.cpf)}). Envie o cartão certo ou corrija o lead.`,
                'error'
            );
            return;
        }

        setCnpjCardName(result.name);
        setAddress((prev) => ({ ...prev, ...result.address }));
        showAlert('Dados do cartão CNPJ preenchidos. Confira o estado.', 'success');
    };

    const getStepError = (index) => {
        if (index === 0) {
            if (isPessoaJuridica && !isCnpjComplete(leadData.cpf)) return 'O CNPJ do lead está incompleto. Corrija no lead.';
            if (!isPessoaJuridica && !isCpfComplete(leadData.cpf)) return 'O CPF do lead está incompleto. Corrija no lead.';

            const missing = [
                ...getMissingAddressFields(address),
                ...(isPessoaJuridica ? getMissingRepresentativeFields(representative) : [])
            ];
            if (missing.length > 0) return `Preencha ${joinLabels(missing)}.`;
        }

        if (index === 1) {
            if (selectedUnits.length === 0) return 'Inclua ao menos uma unidade no contrato.';
            if (!classification) return 'Escolha a classificação.';
        }

        if (index === 2) {
            const missing = [
                !verificationType && 'o token de verificação',
                !isFilled(contactName) && 'o nome do contato'
            ].filter(Boolean);
            if (missing.length > 0) return `Informe ${joinLabels(missing)}.`;
        }

        return '';
    };

    const goToStep = (target) => {
        for (let index = 0; index < target; index += 1) {
            const error = getStepError(index);
            if (error) {
                setStep(index);
                showAlert(error, 'error');
                return;
            }
        }
        setStep(target);
        setMaxStepReached((prev) => Math.max(prev, target));
    };

    const sendContract = async () => {
        if (isSubmitting) return;

        for (let index = 0; index < CONTRACT_STEPS.length; index += 1) {
            const error = getStepError(index);
            if (error) {
                setStep(index);
                showAlert(error, 'error');
                return;
            }
        }

        setIsSubmitting(true);
        const response = await submitContract(buildContractFormData({
            leadData,
            clientType,
            clientName,
            address,
            maritalStatus,
            representative,
            classification,
            verificationType,
            contactName: contactName.trim(),
            phone2,
            units: selectedUnits,
            notificarCliente: canChooseClientNotification ? notificarCliente : true,
            files
        }));
        setIsSubmitting(false);

        if (response?.status === 'success') {
            queryClient.invalidateQueries({ queryKey: ['crm', 'leads'] });
            goToLead({ leadNotice: { message: response.message ?? 'Contrato enviado.', status: 'success' } });
            return;
        }

        showAlert(response?.message ?? 'Não foi possível gerar o contrato.', response?.status ?? 'error');
    };

    if (!isSerasaApproved) return null;

    if (loadState !== 'ready' || !leadData) {
        return (
            <div className='mainBox crm-lead-details-page'>
                <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />
                <div className='crm-lead-details-shell'>
                    <div className='crm-lead-details-card crm-lead-details-load-state' role='status'>
                        {loadState === 'loading'
                            ? (
                                <>
                                    <CircularProgress size={28} />
                                    <span className='crm-lead-details-load-copy'>Carregando lead #{leadId}...</span>
                                </>
                                )
                            : (
                                <>
                                    <strong className='crm-lead-details-load-title'>
                                        {loadState === 'not_found' ? `Lead #${leadId} não encontrado` : 'Não foi possível carregar o lead'}
                                    </strong>
                                    <div className='crm-lead-details-load-actions'>
                                        {loadState !== 'not_found' && (
                                            <button
                                                type='button'
                                                className='crm-lead-details-button crm-lead-details-button-primary'
                                                onClick={() => setReloadKey((prev) => prev + 1)}
                                            >
                                                <RefreshRoundedIcon fontSize='inherit' />
                                                <span>Tentar novamente</span>
                                            </button>
                                        )}
                                        <button
                                            type='button'
                                            className='crm-lead-details-button crm-lead-details-button-secondary'
                                            onClick={() => goToLead()}
                                        >
                                            <ArrowBackRoundedIcon fontSize='inherit' />
                                            <span>Voltar para o lead</span>
                                        </button>
                                    </div>
                                </>
                                )}
                    </div>
                </div>
            </div>
        );
    }

    const fixOnLeadButton = (
        <button
            type='button'
            className='crm-lead-details-button crm-lead-details-button-ghost is-compact'
            onClick={() => goToLead()}
        >
            <EditRoundedIcon fontSize='inherit' />
            <span>Corrigir no lead</span>
        </button>
    );

    const editUnitsButton = (
        <button
            type='button'
            className='crm-lead-details-button crm-lead-details-button-ghost is-compact'
            onClick={() => goToLead({ initialTab: 'unidades' })}
        >
            <EditRoundedIcon fontSize='inherit' />
            <span>Editar na aba Unidades do lead</span>
        </button>
    );

    const renderAddressFields = () => {
        const stateOptions = (isPessoaJuridica ? ESTADOS_BRASIL : ESTADOS_ATENDIDOS).map((uf) => ({ value: uf, label: uf }));
        const cityOptions = CIDADES_POR_ESTADO[address.state] ?? [];

        return (
            <div className='crm-lead-details-grid'>
                <div className='crm-lead-details-field is-small'>
                    <TextField
                        className={editableFieldClass}
                        label='CEP'
                        value={address.cep}
                        onChange={(e) => updateAddress('cep', maskCEP(e.target.value))}
                        inputProps={{ maxLength: 9 }}
                        required
                        fullWidth
                    />
                </div>
                <div className='crm-lead-details-field is-small crm-lead-contract-inline-action'>
                    <button
                        type='button'
                        className='crm-lead-details-button crm-lead-details-button-secondary'
                        onClick={searchCep}
                        disabled={isFetchingCep}
                    >
                        {isFetchingCep ? <CircularProgress size={16} /> : <SearchRoundedIcon fontSize='inherit' />}
                        <span>Buscar CEP</span>
                    </button>
                </div>
                <div className='crm-lead-details-field is-large'>
                    <TextField
                        className={editableFieldClass}
                        label='Rua'
                        value={address.street}
                        onChange={(e) => updateAddress('street', e.target.value)}
                        required
                        fullWidth
                    />
                </div>
                <div className='crm-lead-details-field is-tiny'>
                    <TextField
                        className={editableFieldClass}
                        label='Número'
                        value={address.number}
                        onChange={(e) => updateAddress('number', e.target.value)}
                        required
                        fullWidth
                    />
                </div>
                <div className='crm-lead-details-field is-tiny'>
                    <TextField
                        className={editableFieldClass}
                        label='Compl.'
                        value={address.complement}
                        onChange={(e) => updateAddress('complement', e.target.value)}
                        fullWidth
                    />
                </div>
                <div className='crm-lead-details-field is-small'>
                    <TextField
                        className={editableFieldClass}
                        label='Bairro'
                        value={address.neighborhood}
                        onChange={(e) => updateAddress('neighborhood', e.target.value)}
                        required
                        fullWidth
                    />
                </div>
                <div className='crm-lead-details-field is-tiny'>
                    <SelectField
                        id='contract-state'
                        label='UF'
                        value={address.state}
                        options={stateOptions}
                        onChange={(value) => setAddress((prev) => ({
                            ...prev,
                            state: value,
                            city: isPessoaJuridica || (CIDADES_POR_ESTADO[value] ?? []).includes(prev.city) ? prev.city : ''
                        }))}
                        required
                    />
                </div>
                <div className='crm-lead-details-field is-medium'>
                    {isPessoaJuridica
                        ? (
                            <TextField
                                className={editableFieldClass}
                                label='Cidade'
                                value={address.city}
                                onChange={(e) => updateAddress('city', e.target.value)}
                                required
                                fullWidth
                            />
                            )
                        : (
                            <Autocomplete
                                options={cityOptions}
                                value={address.city || null}
                                onChange={(_, value) => updateAddress('city', value ?? '')}
                                isOptionEqualToValue={(option, value) => option === value}
                                noOptionsText={address.state ? 'Nenhuma cidade encontrada' : 'Escolha a UF para ver as cidades'}
                                popupIcon={<KeyboardArrowDownRoundedIcon />}
                                renderInput={(params) => (
                                    <TextField {...params} className={editableFieldClass} label='Cidade' required />
                                )}
                            />
                            )}
                </div>
            </div>
        );
    };

    const renderClientStep = () => (
        <div className='crm-client-stack'>
            <Block icon={PersonRoundedIcon} title='Dados do lead' actions={fixOnLeadButton}>
                <div className='crm-client-field-grid'>
                    <Field label={isPessoaJuridica ? 'Nome no lead' : 'Nome'} value={displayValue(leadData.nome)} wide />
                    <Field label={isPessoaJuridica ? 'CNPJ' : 'CPF'} value={displayValue(maskCpfCnpj(leadData.cpf ?? ''))} />
                    <Field label='E-mail' value={displayValue(leadData.email)} />
                    <Field label='Telefone' value={displayValue(maskPhone(getPhoneDigits(leadData.telefone)))} />
                    <Field label='Cupom' value={getLeadCoupon(leadData.cupom) || 'Sem cupom'} />
                    <Field label='Desconto' value={`${getLeadDiscountPercent(leadData.desconto)}%`} />
                </div>
            </Block>

            {isPessoaJuridica && (
                <Block icon={UploadFileRoundedIcon} title='Cartão CNPJ'>
                    <p className='crm-client-block-empty'>
                        Envie o cartão CNPJ para preencher a razão social e o endereço da empresa.
                    </p>
                    <div className='crm-lead-contract-upload-row'>
                        <FileField label='Cartão CNPJ' file={files.cnpj} onChange={(file) => updateFile('cnpj', file)} />
                        <button
                            type='button'
                            className='crm-lead-details-button crm-lead-details-button-primary'
                            onClick={readCnpjCard}
                            disabled={isFetchingCnpj}
                        >
                            {isFetchingCnpj ? <CircularProgress size={16} /> : <SearchRoundedIcon fontSize='inherit' />}
                            <span>{isFetchingCnpj ? 'Lendo cartão...' : 'Buscar dados'}</span>
                        </button>
                    </div>
                    {cnpjCardName && (
                        <div className='crm-client-field-grid'>
                            <Field label='Razão social (cartão CNPJ)' value={cnpjCardName} wide />
                        </div>
                    )}
                </Block>
            )}

            <Block
                icon={LocationOnRoundedIcon}
                title={isPessoaJuridica ? 'Endereço da empresa' : 'Endereço do cliente'}
            >
                {!isPessoaJuridica && (
                    <p className='crm-client-block-empty'>
                        {units[0]?.endereco
                            ? 'Preenchido com o endereço da 1ª unidade do lead. Ajuste se o cliente morar em outro lugar.'
                            : 'Informe o endereço do cliente.'}
                    </p>
                )}
                {renderAddressFields()}
            </Block>

            {isPessoaJuridica
                ? (
                    <Block icon={BadgeRoundedIcon} title='Representante'>
                        <div className='crm-lead-details-grid'>
                            <div className='crm-lead-details-field is-large'>
                                <TextField
                                    className={editableFieldClass}
                                    label='Nome do representante'
                                    value={representative.name}
                                    onChange={(e) => updateRepresentative('name', e.target.value)}
                                    required
                                    fullWidth
                                />
                            </div>
                            <div className='crm-lead-details-field is-small'>
                                <TextField
                                    className={editableFieldClass}
                                    label='CPF do representante'
                                    value={representative.cpf}
                                    onChange={(e) => updateRepresentative('cpf', maskCpfCnpj(e.target.value))}
                                    inputProps={{ maxLength: 14 }}
                                    required
                                    fullWidth
                                />
                            </div>
                            <div className='crm-lead-details-field is-large'>
                                <TextField
                                    className={editableFieldClass}
                                    label='Endereço do representante'
                                    value={representative.address}
                                    onChange={(e) => updateRepresentative('address', e.target.value)}
                                    required
                                    fullWidth
                                />
                            </div>
                            <div className='crm-lead-details-field is-small'>
                                <TextField
                                    className={editableFieldClass}
                                    label='CEP do representante'
                                    value={representative.cep}
                                    onChange={(e) => updateRepresentative('cep', maskCEP(e.target.value))}
                                    inputProps={{ maxLength: 9 }}
                                    required
                                    fullWidth
                                />
                            </div>
                            <div className='crm-lead-details-field is-full'>
                                <FileField label='Contrato social' file={files.contrato} onChange={(file) => updateFile('contrato', file)} />
                            </div>
                        </div>
                    </Block>
                    )
                : (
                    <Block icon={BadgeRoundedIcon} title='Estado civil'>
                        <div className='crm-lead-details-grid'>
                            <div className='crm-lead-details-field is-small'>
                                <SelectField
                                    id='contract-marital-status'
                                    label='Estado civil'
                                    value={maritalStatus}
                                    options={MARITAL_STATUS_OPTIONS.map((option) => ({ value: option, label: option }))}
                                    onChange={setMaritalStatus}
                                />
                            </div>
                        </div>
                    </Block>
                    )}
        </div>
    );

    const renderUnitsStep = () => (
        <div className='crm-client-stack'>
            <Block
                icon={HomeWorkRoundedIcon}
                title={(
                    <>
                        Unidades do lead
                        <span className='crm-client-block-count'>{selectedUnits.length}/{units.length}</span>
                    </>
                )}
                actions={editUnitsButton}
            >
                <ul className='crm-lead-contract-units'>
                    {units.map((unit, index) => {
                        const key = getUnitKey(unit, index);
                        const isSelected = selectedUnitKeys.includes(key);

                        return (
                            <li key={key} className={`crm-lead-contract-unit${isSelected ? ' is-selected' : ''}`}>
                                <label className='crm-lead-contract-unit-label'>
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => toggleUnit(key)}
                                        inputProps={{ 'aria-label': `Incluir unidade ${unit.codigo_do_cliente ?? ''} no contrato` }}
                                    />
                                    <span className='crm-lead-contract-unit-body'>
                                        <span className='crm-lead-contract-unit-title'>
                                            CC {displayValue(unit.codigo_do_cliente)}
                                            {unit.alta_tensao && <Pill tone='is-warning'>Alta tensão</Pill>}
                                        </span>
                                        <span className='crm-lead-contract-unit-address'>
                                            {displayValue(formatUnitAddress(unit.endereco))}
                                        </span>
                                    </span>
                                    <span className='crm-lead-contract-unit-meta'>
                                        <strong>{formatConsumption(unit.consumo_medio)}</strong>
                                        <span>{getDistribuidoraFromState(unit.endereco?.estado) || 'Distribuidora não identificada'}</span>
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            </Block>

            <Block icon={BoltRoundedIcon} title='Condições'>
                <div className='crm-lead-details-grid'>
                    <div className='crm-lead-details-field is-medium'>
                        <SelectField
                            id='contract-classification'
                            label='Classificação'
                            value={classification}
                            options={CLASSIFICATION_OPTIONS}
                            onChange={(value) => {
                                setClassification(value);
                                setIsClassificationTouched(true);
                            }}
                            required
                        />
                    </div>
                    <div className='crm-lead-details-field is-medium'>
                        <p className='crm-lead-contract-hint'>
                            {isClassificationTouched
                                ? 'Classificação escolhida manualmente.'
                                : selectedUnits.some((unit) => unit.alta_tensao)
                                    ? 'Alta tensão porque alguma unidade incluída é de alta tensão.'
                                    : 'Baixa tensão porque nenhuma unidade incluída é de alta tensão.'}
                        </p>
                    </div>
                </div>
                <div className='crm-client-field-grid'>
                    <Field label='Desconto' value={`${getLeadDiscountPercent(leadData.desconto)}%`} />
                    <Field label='Cupom' value={getLeadCoupon(leadData.cupom) || 'Sem cupom'} />
                    <Field label='Consumo das unidades incluídas' value={formatConsumption(selectedConsumption)} />
                </div>
            </Block>
        </div>
    );

    const renderSendStep = () => (
        <div className='crm-client-stack'>
            <Block icon={VerifiedUserRoundedIcon} title='Assinatura'>
                <div className='crm-lead-details-grid'>
                    <div className='crm-lead-details-field is-small'>
                        <SelectField
                            id='contract-verification'
                            label='Token de verificação'
                            value={verificationType}
                            options={VERIFICATION_OPTIONS}
                            onChange={setVerificationType}
                            required
                        />
                    </div>
                    <div className='crm-lead-details-field is-small'>
                        <TextField
                            className={editableFieldClass}
                            label='Nome do contato'
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            required
                            fullWidth
                        />
                    </div>
                    <div className='crm-lead-details-field is-small'>
                        <TextField
                            className={editableFieldClass}
                            label='Telefone 2 (opcional)'
                            value={phone2}
                            onChange={(e) => setPhone2(maskPhone(e.target.value))}
                            inputProps={{ maxLength: 15 }}
                            fullWidth
                        />
                    </div>
                    <div className='crm-lead-details-field is-full'>
                        <FileField
                            label={isPessoaJuridica ? 'CNH do representante' : 'CNH'}
                            file={files.cnh}
                            onChange={(file) => updateFile('cnh', file)}
                        />
                    </div>
                    {canChooseClientNotification && (
                        <div className='crm-lead-details-field is-full'>
                            <FormControlLabel
                                sx={{ marginLeft: 0 }}
                                control={(
                                    <Switch
                                        checked={notificarCliente}
                                        onChange={() => setNotificarCliente((prev) => !prev)}
                                    />
                                )}
                                label={notificarCliente ? 'Notificar cliente' : 'Não notificar cliente'}
                            />
                        </div>
                    )}
                </div>
            </Block>

            <Block icon={CheckRoundedIcon} title='Resumo'>
                <div className='crm-client-field-grid'>
                    <Field label='Cliente' value={displayValue(clientName)} wide />
                    <Field label={isPessoaJuridica ? 'CNPJ' : 'CPF'} value={displayValue(maskCpfCnpj(leadData.cpf ?? ''))} />
                    {!isPessoaJuridica && <Field label='Estado civil' value={displayValue(maritalStatus)} />}
                    <Field label='Endereço' value={displayValue(formatAddressLine(address))} wide />
                    {isPessoaJuridica && (
                        <Field
                            label='Representante'
                            value={displayValue([representative.name, representative.cpf].filter(isFilled).join(' · '))}
                            wide
                        />
                    )}
                    <Field label='E-mail' value={displayValue(leadData.email)} />
                    <Field label='Telefone' value={displayValue(maskPhone(getPhoneDigits(leadData.telefone)))} />
                    <Field
                        label='Unidades'
                        value={`${selectedUnits.length} de ${units.length} · ${formatConsumption(selectedConsumption)}`}
                    />
                    <Field label='Classificação' value={getClassificationLabel(classification)} />
                    <Field label='Desconto' value={`${getLeadDiscountPercent(leadData.desconto)}%`} />
                    <Field label='Cupom' value={getLeadCoupon(leadData.cupom) || 'Sem cupom'} />
                    <Field label='Token' value={getVerificationLabel(verificationType)} />
                    <Field
                        label='Documentos'
                        value={displayValue([
                            files.cnh && 'CNH',
                            files.cnpj && 'Cartão CNPJ',
                            files.contrato && 'Contrato social'
                        ].filter(Boolean).join(', '))}
                    />
                    {canChooseClientNotification && (
                        <Field label='Notificação' value={notificarCliente ? 'Notificar cliente' : 'Não notificar'} />
                    )}
                </div>
            </Block>
        </div>
    );

    const isLastStep = step === CONTRACT_STEPS.length - 1;

    return (
        <div className='mainBox crm-lead-details-page crm-lead-contract-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-lead-details-shell crm-lead-contract-shell'>
                <header className='crm-lead-details-header'>
                    <button
                        type='button'
                        className='crm-lead-details-back'
                        onClick={() => goToLead()}
                        aria-label='Voltar para o lead'
                        title='Voltar para o lead'
                    >
                        <ArrowBackRoundedIcon fontSize='inherit' />
                    </button>

                    <div className='crm-lead-details-heading'>
                        <span className='crm-lead-details-kicker'>Lead #{leadData.lead_id} · Gerar contrato</span>
                        <h1 className='crm-lead-details-title'>{leadData.nome}</h1>
                        <div className='crm-lead-details-badges'>
                            <span className='crm-lead-details-meta'>
                                <BadgeRoundedIcon fontSize='inherit' />
                                {isPessoaJuridica ? 'Pessoa jurídica' : 'Pessoa física'}
                            </span>
                            <span className='crm-lead-details-meta'>
                                <PersonRoundedIcon fontSize='inherit' />
                                {leadData.vendedor || 'Sem vendedor'}
                            </span>
                            <span className='crm-lead-details-meta'>
                                <VerifiedUserRoundedIcon fontSize='inherit' />
                                Serasa aprovado
                            </span>
                        </div>
                    </div>
                </header>

                <div className='crm-lead-details-card'>
                    <ol className='crm-lead-contract-steps' aria-label='Etapas do contrato'>
                        {CONTRACT_STEPS.map((item, index) => {
                            const isActive = index === step;
                            const isDone = index < step;
                            const canOpen = index <= maxStepReached && !isSubmitting;

                            return (
                                <li key={item.key} className='crm-lead-contract-step-item'>
                                    <button
                                        type='button'
                                        className={`crm-lead-contract-step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
                                        onClick={() => goToStep(index)}
                                        disabled={!canOpen}
                                        aria-current={isActive ? 'step' : undefined}
                                    >
                                        <span className='crm-lead-contract-step-index'>
                                            {isDone ? <CheckRoundedIcon fontSize='inherit' /> : index + 1}
                                        </span>
                                        <span className='crm-lead-contract-step-label'>{item.label}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>

                    <div className='crm-lead-contract-body'>
                        {step === 0 && renderClientStep()}
                        {step === 1 && renderUnitsStep()}
                        {step === 2 && renderSendStep()}
                    </div>

                    <footer className='crm-lead-contract-footer'>
                        <button
                            type='button'
                            className='crm-lead-details-button crm-lead-details-button-secondary'
                            onClick={() => (step === 0 ? goToLead() : setStep(step - 1))}
                            disabled={isSubmitting}
                        >
                            <ArrowBackRoundedIcon fontSize='inherit' />
                            <span>{step === 0 ? 'Voltar para o lead' : 'Voltar'}</span>
                        </button>

                        {isLastStep
                            ? (
                                <button
                                    type='button'
                                    className='crm-lead-details-button crm-lead-details-button-primary'
                                    onClick={sendContract}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? <CircularProgress size={16} /> : <SendRoundedIcon fontSize='inherit' />}
                                    <span>{isSubmitting ? 'Enviando contrato...' : 'Enviar contrato'}</span>
                                </button>
                                )
                            : (
                                <button
                                    type='button'
                                    className='crm-lead-details-button crm-lead-details-button-primary'
                                    onClick={() => goToStep(step + 1)}
                                >
                                    <span>Continuar</span>
                                    <ArrowForwardRoundedIcon fontSize='inherit' />
                                </button>
                                )}
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default CrmLeadContract;
